import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';

// ─── API Setup ──────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// ─── Interfaces ─────────────────────────────────────────────────────────────
interface CampaignUser {
  _id: string;
  userId: string;
  name: string;
  email: string;
  phone: string;
  applicationId?: string;
  applicationStatus: string;
  applicationProgress: { percent: number; step: string };
  emailStage: string;
  lastEmailSentAt?: string;
  campaignActive: boolean;
  createdAt: string;
  excluded?: boolean;
  excludedAt?: string;
  excludedReason?: string;
  excludedNote?: string;
}

interface FunnelStats {
  totalAbandoned: number;
  totalExcluded: number;
  emailsSent: number;
  emailsOpened: number;
  linksClicked: number;
  portalVisits: number;
  applicationsCreated: number;
  paymentsCompleted: number;
}

interface LiveMonitorData {
  currentlyFilling: any[];
  stuckAtDocuments: any[];
  stuckAtPayment: any[];
}

interface RecoverySettings {
  enabled: boolean;
  scanIntervalHours: number;
  reminder1DelayDays: number;
  reminder2DelayDays: number;
  finalReminderDelayDays: number;
  abandonmentThresholdHours: number;
  lastScanAt?: string;
  lastAutoRunAt?: string;
}

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface EmailEvent {
  _id: string;
  userId: string;
  campaignUserId?: string;
  eventType: string;
  campaignStage?: string;
  recipientEmail?: string;
  recipientName?: string;
  subject?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  userEmail?: string;
  userName?: string;
}

interface TimelineData {
  campaignUser: CampaignUser;
  events: EmailEvent[];
}

// ─── Animations ─────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ─── Styled Components ──────────────────────────────────────────────────────
const PageContainer = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.3s ease;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 20px 0 10px 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MiniCard = styled.div<{ $accent: string }>`
  padding: 12px 14px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #fff 0%, #fafbfc 100%);
  border: 1px solid #e8eaed;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${p => p.$accent};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconWrapper = styled.div<{ $bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
  flex-shrink: 0;
  font-size: 16px;
`;

const MiniCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MiniLabel = styled.div`
  font-size: 10px;
  color: #5f6368;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #202124;
  line-height: 1.2;
  transition: color 0.3s ease;
`;

// ─── Animated Counter Component ──────────────────────────────────────────────
const AnimatedCounter: React.FC<{ value: number; duration?: number }> = ({ value, duration = 800 }) => {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const frameRef = useRef<number>(0);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    prevRef.current = value;

    if (from === to) {
      setDisplay(to);
      return;
    }

    // Flash color: green if going up, red if going down
    setFlash(to > from ? 'up' : 'down');

    const diff = to - from;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + diff * eased);
      setDisplay(current);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplay(to);
        // Clear flash after animation completes + a brief hold
        setTimeout(() => setFlash(null), 400);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, duration]);

  return (
    <span style={{
      color: flash === 'up' ? '#10b981' : flash === 'down' ? '#ef4444' : '#202124',
      transition: 'color 0.3s ease',
    }}>
      {display.toLocaleString('en-IN')}
    </span>
  );
};

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8eaed;
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e8eaed;
`;

const CardHeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardBody = styled.div`
  padding: 18px;
`;

const FunnelStep = styled.div<{ $width: string; $color: string }>`
  width: ${p => p.$width};
  margin: 0 auto 6px auto;
  padding: 10px 16px;
  border-radius: 8px;
  background: ${p => p.$color};
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 180px;

  &:hover {
    filter: brightness(1.05);
    transform: scale(1.01);
  }
`;

const FunnelLabel = styled.span`
  font-weight: 600;
`;

const FunnelMeta = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  opacity: 0.9;
`;

const MonitorSection = styled.div`
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
`;

const MonitorHeader = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: ${p => p.$color};
  border-radius: 8px 8px 0 0;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const CountBadge = styled.span`
  background: rgba(255, 255, 255, 0.25);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
`;

const MonitorList = styled.div`
  border: 1px solid #e8eaed;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 130px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const MonitorItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 14px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 12px;
  color: #475569;

  &:last-child { border-bottom: none; }
`;

const MonitorEmpty = styled.div`
  padding: 12px 14px;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  font-style: italic;
`;

const LiveDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  animation: ${pulse} 2s ease-in-out infinite;
  margin-right: 6px;
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
  align-items: flex-start;
`;

const ActionButton = styled.button<{ $variant?: string }>`
  padding: 10px 20px;
  border: ${p => p.$variant === 'outline' ? '2px solid #94a3b8' : 'none'};
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${p => p.$variant === 'outline' ? '#475569' : '#fff'};
  background: ${p => {
    if (p.$variant === 'outline') return 'transparent';
    if (p.$variant === 'blue') return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    if (p.$variant === 'purple') return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
    if (p.$variant === 'green') return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    return 'linear-gradient(135deg, #64748b 0%, #475569 100%)';
  }};

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    filter: none;
    box-shadow: none;
  }
`;

const SettingsPanel = styled.div`
  background: #f8fafc;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const SettingsField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SettingsLabel = styled.label`
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const NumberInput = styled.input`
  width: 80px;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  background: #fff;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ToggleSwitch = styled.div<{ $active: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: ${p => p.$active ? '#10b981' : '#cbd5e1'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.$active ? '23px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: left 0.2s ease;
  }
`;

const PulseDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 6px #10b981;
  animation: ${pulse} 2s ease-in-out infinite;
  flex-shrink: 0;
`;

const TimestampText = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`;

const TableCard = styled(Card)`
  margin-bottom: 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th`
  padding: 10px 14px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 2px solid #e8eaed;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Td = styled.td`
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.15s ease;
  &:hover { background: #f8fafc; }
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  white-space: nowrap;
`;

const StageBadge = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: ${p => p.$color};
  background: ${p => p.$color}15;
  border: 1px solid ${p => p.$color}30;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #e2e8f0;
  min-width: 50px;
`;

const ProgressBarFill = styled.div<{ $percent: number; $color: string }>`
  height: 100%;
  border-radius: 3px;
  background: ${p => p.$color};
  width: ${p => Math.min(p.$percent, 100)}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  min-width: 32px;
`;

const SmallButton = styled.button`
  padding: 5px 12px;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
`;

const ToastContainer = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  padding: 0.85rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  background-color: ${p =>
    p.$type === 'success' ? '#f0fff4' : p.$type === 'error' ? '#fff5f5' : '#eff6ff'};
  color: ${p =>
    p.$type === 'success' ? '#22543d' : p.$type === 'error' ? '#742a2a' : '#1e40af'};
  border: 1px solid ${p =>
    p.$type === 'success' ? '#9ae6b4' : p.$type === 'error' ? '#feb2b2' : '#93c5fd'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${slideInRight} 0.3s ease-out;
`;

// ─── Pagination Styled Components ───────────────────────────────────────────
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-top: 1px solid #e8eaed;
  background: #f8fafc;
  flex-wrap: wrap;
  gap: 10px;
`;

const PaginationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #64748b;
`;

const PageSizeSelect = styled.select`
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: #475569;
  background: #fff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 5px 10px;
  border: 1px solid ${p => p.$active ? '#3b82f6' : '#d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${p => p.$active ? '600' : '400'};
  background: ${p => p.$active ? '#3b82f6' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#475569'};
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 32px;
  text-align: center;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: ${p => p.$active ? '#fff' : '#3b82f6'};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ─── Live Log ───────────────────────────────────────────────────────────────
const LogPanel = styled.div`
  background: #0f172a;
  border-radius: 12px;
  border: 1px solid #1e293b;
  margin-bottom: 16px;
  overflow: hidden;
`;

const LogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #1e293b;
  border-bottom: 1px solid #334155;
`;

const LogHeaderTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogBody = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding: 10px 0;
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #0f172a; }
  &::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
`;

const LogEntry = styled.div<{ $color: string }>`
  padding: 2px 16px;
  font-size: 11.5px;
  line-height: 1.7;
  color: ${p => p.$color};
  white-space: pre-wrap;
  word-break: break-word;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const LogTime = styled.span`
  color: #475569;
  margin-right: 8px;
  font-size: 10px;
`;

// ─── Refresh Icon ────────────────────────────────────────────────────────────
const RefreshIcon = styled.button<{ $spinning: boolean }>`
  background: none;
  border: none;
  cursor: ${p => p.$spinning ? 'not-allowed' : 'pointer'};
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$spinning ? 0.5 : 0.6};
  transition: all 0.2s;
  font-size: 14px;
  color: #64748b;

  &:hover:not(:disabled) { opacity: 1; }

  svg {
    animation: ${p => p.$spinning ? spin : 'none'} 1s linear infinite;
  }
`;

// ─── Collapsible Card Header ─────────────────────────────────────────────────
const CollapsibleCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e8eaed;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;

  &:hover { background: #f1f5f9; }
`;

const ChevronIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  font-size: 12px;
  color: #94a3b8;
`;

// ─── Section Header with Pagination ─────────────────────────────────────────
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0 10px 0;
  flex-wrap: wrap;
  gap: 8px;
`;

const InlinePaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ─── Event Type Badge ───────────────────────────────────────────────────────
const EventBadge = styled.span<{ $color: string; $bg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  white-space: nowrap;
`;

// ─── Modal / Overlay ────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 680px;
  max-width: 95vw;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e8eaed;
  background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
`;

const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover { background: #e2e8f0; color: #1e293b; }
`;

// ─── Timeline Styled ────────────────────────────────────────────────────────
const TimelineContainer = styled.div`
  position: relative;
  padding-left: 28px;

  &::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 16px;
  padding-left: 16px;

  &:last-child { margin-bottom: 0; }
`;

const TimelineDot = styled.div<{ $color: string }>`
  position: absolute;
  left: -22px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${p => p.$color};
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px ${p => p.$color}40;
`;

const TimelineTime = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 2px;
`;

const TimelineContent = styled.div`
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function timeAgo(dateString: string | undefined): string {
  if (!dateString) return 'N/A';
  const now = Date.now();
  const then = new Date(dateString).getTime();
  if (isNaN(then)) return 'N/A';
  const diff = now - then;
  if (diff < 0) return 'just now';
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatDate(dateString: string): string {
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function getStatusStyle(status: string): { color: string; bg: string } {
  switch (status) {
    case 'completed': return { color: '#166534', bg: '#dcfce7' };
    case 'payment_pending': return { color: '#991b1b', bg: '#fef2f2' };
    case 'document_pending': return { color: '#9a3412', bg: '#fff7ed' };
    case 'application_started': return { color: '#1e40af', bg: '#eff6ff' };
    case 'no_application':
    default: return { color: '#475569', bg: '#f1f5f9' };
  }
}

function getStageColor(stage: string): string {
  switch (stage) {
    case 'reminder_1': return '#3b82f6';
    case 'reminder_2': return '#f59e0b';
    case 'final_reminder': return '#ef4444';
    case 'none':
    default: return '#94a3b8';
  }
}

function getProgressColor(percent: number): string {
  if (percent >= 80) return '#10b981';
  if (percent >= 50) return '#f59e0b';
  if (percent >= 20) return '#3b82f6';
  return '#94a3b8';
}

function getEventStyle(eventType: string): { color: string; bg: string; icon: string; label: string } {
  switch (eventType) {
    case 'email_sent': return { color: '#1e40af', bg: '#dbeafe', icon: '\u2709', label: 'Email Sent' };
    case 'email_open': return { color: '#166534', bg: '#dcfce7', icon: '\uD83D\uDCE8', label: 'Email Opened' };
    case 'email_click': return { color: '#7c3aed', bg: '#ede9fe', icon: '\uD83D\uDD17', label: 'Link Clicked' };
    case 'portal_visit': return { color: '#0891b2', bg: '#cffafe', icon: '\uD83C\uDF10', label: 'Portal Visit' };
    case 'application_created': return { color: '#ea580c', bg: '#fff7ed', icon: '\uD83D\uDCDD', label: 'App Created' };
    case 'payment_completed': return { color: '#16a34a', bg: '#f0fdf4', icon: '\u2705', label: 'Payment Done' };
    case 'outreach_sent': return { color: '#d97706', bg: '#fffbeb', icon: '\uD83D\uDCE7', label: 'Outreach Sent' };
    case 'outreach_feedback': return { color: '#be185d', bg: '#fdf2f8', icon: '\uD83D\uDCAC', label: 'User Feedback' };
    case 'outreach_open': return { color: '#065f46', bg: '#ecfdf5', icon: '\uD83D\uDC41', label: 'Outreach Opened' };
    default: return { color: '#64748b', bg: '#f1f5f9', icon: '\u2022', label: eventType };
  }
}

function getPageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }
  return pages;
}

// ─── Component ──────────────────────────────────────────────────────────────
const AdminApplicationRecovery: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<CampaignUser[]>([]);
  const [funnel, setFunnel] = useState<FunnelStats>({
    totalAbandoned: 0, totalExcluded: 0, emailsSent: 0, emailsOpened: 0, linksClicked: 0,
    portalVisits: 0, applicationsCreated: 0, paymentsCompleted: 0,
  });
  const [liveData, setLiveData] = useState<LiveMonitorData>({
    currentlyFilling: [], stuckAtDocuments: [], stuckAtPayment: [],
  });
  const [settings, setSettings] = useState<RecoverySettings>({
    enabled: false, scanIntervalHours: 6, reminder1DelayDays: 1,
    reminder2DelayDays: 3, finalReminderDelayDays: 7, abandonmentThresholdHours: 24,
  });
  const [toast, setToast] = useState<ToastState>({ show: false, message: '', type: 'info' });
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [schedulerRunning, setSchedulerRunning] = useState<boolean | null>(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [testLoading, setTestLoading] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<{ stage: string; success: boolean; sentTo?: string[] }[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Audit trail
  const [activityLog, setActivityLog] = useState<EmailEvent[]>([]);
  const [activityLogTotal, setActivityLogTotal] = useState(0);
  const [activityLogPage, setActivityLogPage] = useState(1);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [activityEventFilter, setActivityEventFilter] = useState('');

  // User timeline modal
  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Live log
  interface LogLine { time: string; message: string; type: string }
  const [liveLog, setLiveLog] = useState<LogLine[]>([]);
  const [liveLogActive, setLiveLogActive] = useState(false);
  const logEndRef = useRef<HTMLDivElement>(null);

  // SSE abort controller for kill button
  const sseAbortRef = useRef<AbortController | null>(null);

  // Ref to always have latest log lines (for saving on kill/complete)
  const liveLogRef = useRef<LogLine[]>([]);

  // Test card collapsible
  const [testCardExpanded, setTestCardExpanded] = useState(false);

  // Tile detail modal
  const [tileModalData, setTileModalData] = useState<{ title: string; metric: string; items: any[]; total: number } | null>(null);
  const [tileModalLoading, setTileModalLoading] = useState(false);

  // Campaign users refresh loading
  const [refreshingUsers, setRefreshingUsers] = useState(false);

  // Exclude user state
  const [excludeModalUser, setExcludeModalUser] = useState<CampaignUser | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [excludeNote, setExcludeNote] = useState('');
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false); // toggle to show/hide excluded users
  const [campaignSearch, setCampaignSearch] = useState(''); // inline search for campaign users

  // Operation log history
  interface OperationLog {
    _id: string;
    operationType: string;
    status: string;
    summary: string;
    sent: number;
    failed: number;
    total: number;
    lines: LogLine[];
    createdAt: string;
  }
  const [logHistory, setLogHistory] = useState<OperationLog[]>([]);
  const [logHistoryVisible, setLogHistoryVisible] = useState(false);
  const [logHistoryLoading, setLogHistoryLoading] = useState(false);
  const [viewingLog, setViewingLog] = useState<OperationLog | null>(null);

  // Overview tiles refresh
  const [refreshingOverview, setRefreshingOverview] = useState(false);

  const liveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/dashboard`, { withCredentials: true });
      const data = res.data;
      if (data.funnel) setFunnel(data.funnel);
      if (data.users) {
        setUsers(data.users);
        setCurrentPage(1); // reset page on data refresh
      }
      if (data.settings) setSettings(prev => ({ ...prev, ...data.settings }));
      if (data.schedulerRunning !== undefined) setSchedulerRunning(data.schedulerRunning);
    } catch (err: any) {
      console.error('Failed to fetch recovery dashboard:', err);
      showToast('Failed to load dashboard data', 'error');
    }
  }, [showToast]);

  const fetchLiveMonitor = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/live-monitor`, { withCredentials: true });
      if (res.data) setLiveData(res.data);
    } catch (err: any) {
      console.error('Failed to fetch live monitor:', err);
    }
  }, []);

  const fetchActivityLog = useCallback(async (page: number = 1, eventType: string = '') => {
    setActivityLogLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (eventType) params.eventType = eventType;
      const res = await axios.get(`${API_BASE}/api/admin/recovery/activity-log`, { params, withCredentials: true });
      if (res.data?.events) {
        setActivityLog(res.data.events);
        setActivityLogTotal(res.data.total || 0);
        setActivityLogPage(res.data.page || 1);
      }
    } catch (err: any) {
      console.error('Failed to fetch activity log:', err);
    } finally {
      setActivityLogLoading(false);
    }
  }, []);

  const fetchUserTimeline = useCallback(async (userId: string) => {
    if (!userId || userId === 'undefined') {
      showToast('No user linked to this event', 'info');
      return;
    }
    setTimelineLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/user-timeline/${userId}`, { withCredentials: true });
      if (res.data?.campaignUser) {
        setTimelineData({ campaignUser: res.data.campaignUser, events: res.data.events || [] });
      } else if (res.data?.events) {
        // For outreach events that may not have a campaign user record
        setTimelineData({ campaignUser: { _id: userId, userId, name: '', email: res.data.events[0]?.recipientEmail || 'Unknown', phone: '', applicationStatus: 'unknown', applicationProgress: { percent: 0, step: '' }, emailStage: 'none', campaignActive: false, createdAt: '' } as any, events: res.data.events });
      }
    } catch (err: any) {
      showToast('Failed to load user timeline', 'error');
    } finally {
      setTimelineLoading(false);
    }
  }, [showToast]);

  const fetchTileDetail = useCallback(async (metric: string) => {
    setTileModalLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/tile-detail/${metric}`, { withCredentials: true });
      if (res.data) {
        setTileModalData({ title: res.data.title, metric, items: res.data.items || [], total: res.data.total || 0 });
      }
    } catch (err: any) {
      showToast('Failed to load detail', 'error');
    } finally {
      setTileModalLoading(false);
    }
  }, [showToast]);

  const handleRefreshUsers = useCallback(async () => {
    setRefreshingUsers(true);
    try {
      await fetchDashboard();
      showToast('Campaign users refreshed', 'success');
    } catch {
      showToast('Failed to refresh', 'error');
    } finally {
      setRefreshingUsers(false);
    }
  }, [fetchDashboard, showToast]);

  const handleRefreshOverview = useCallback(async () => {
    setRefreshingOverview(true);
    try {
      await fetchDashboard();
      showToast('Overview metrics refreshed', 'success');
    } catch {
      showToast('Failed to refresh metrics', 'error');
    } finally {
      setRefreshingOverview(false);
    }
  }, [fetchDashboard, showToast]);

  const handleExcludeUser = useCallback(async () => {
    if (!excludeModalUser) return;
    setExcludeLoading(true);
    try {
      await axios.post(`${API_BASE}/api/admin/recovery/exclude-user`, {
        campaignUserId: excludeModalUser._id,
        reason: excludeReason || 'other',
        note: excludeNote,
      }, { withCredentials: true });
      showToast(`${excludeModalUser.email} excluded from recovery`, 'success');
      setExcludeModalUser(null);
      setExcludeReason('');
      setExcludeNote('');
      await fetchDashboard();
    } catch (err: any) {
      showToast('Failed to exclude user', 'error');
    } finally {
      setExcludeLoading(false);
    }
  }, [excludeModalUser, excludeReason, excludeNote, showToast, fetchDashboard]);

  const handleIncludeUser = useCallback(async (user: CampaignUser) => {
    try {
      await axios.post(`${API_BASE}/api/admin/recovery/include-user`, {
        campaignUserId: user._id,
      }, { withCredentials: true });
      showToast(`${user.email} re-included in recovery`, 'success');
      await fetchDashboard();
    } catch (err: any) {
      showToast('Failed to re-include user', 'error');
    }
  }, [showToast, fetchDashboard]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchLiveMonitor(), fetchActivityLog(1)]);
      setLoading(false);
    };
    init();

    liveTimerRef.current = setInterval(fetchLiveMonitor, 15000);
    return () => {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [fetchDashboard, fetchLiveMonitor, fetchActivityLog]);

  // Pagination computed values — filter by exclusion toggle + search
  const excludedCount = users.filter(u => u.excluded).length;
  const filteredUsers = (showExcluded ? users : users.filter(u => !u.excluded)).filter(u => {
    if (!campaignSearch.trim()) return true;
    const q = campaignSearch.toLowerCase().trim();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q) ||
      (u.applicationId || '').toLowerCase().includes(q)
    );
  });
  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / pageSize);
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + pageSize);
  const activityLogTotalPages = Math.ceil(activityLogTotal / 20);

  // Handlers
  const handleScan = async () => {
    setActionLoading('scan');
    try {
      const res = await axios.post(`${API_BASE}/api/admin/recovery/scan`, {}, { withCredentials: true });
      showToast(res.data?.message || 'Scan completed successfully', 'success');
      await fetchDashboard();
      await fetchActivityLog(1, activityEventFilter);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Scan failed', 'error');
    } finally { setActionLoading(null); }
  };

  const addLogLine = useCallback((message: string, type: string) => {
    const now = new Date();
    const time = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    const line = { time, message, type };
    // Update ref IMMEDIATELY (outside state updater) so finally block always has latest lines
    liveLogRef.current = [...liveLogRef.current, line];
    setLiveLog(liveLogRef.current);
  }, []);

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logEndRef.current && liveLogActive) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveLog, liveLogActive]);

  const handleKillOperation = useCallback(() => {
    if (sseAbortRef.current) {
      sseAbortRef.current.abort();
      addLogLine('Kill signal sent — stopping operation...', 'error');
    }
  }, [addLogLine]);

  const saveOperationLog = useCallback(async (operationType: string, status: string, logLines: LogLine[]) => {
    try {
      // Extract summary from last 'complete' or 'error' line
      const lastSummary = [...logLines].reverse().find(l => l.type === 'complete' || (l.type === 'error' && l.message.includes('complete')));
      console.log('[saveOperationLog] Saving:', { operationType, status, linesCount: logLines.length, firstLine: logLines[0], lastLine: logLines[logLines.length - 1] });
      const resp = await axios.post(`${API_BASE}/api/admin/recovery/operation-log`, {
        operationType,
        status,
        summary: lastSummary?.message || `${operationType} operation ${status}`,
        lines: logLines,
      }, { withCredentials: true });
      console.log('[saveOperationLog] Response:', resp.data);
    } catch (err: any) {
      console.error('Failed to save operation log:', err?.response?.data || err.message || err);
    }
  }, []);

  const fetchLogHistory = useCallback(async () => {
    setLogHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/operation-logs?limit=20`, { withCredentials: true });
      if (res.data?.logs) setLogHistory(res.data.logs);
    } catch (err) {
      console.error('Failed to fetch log history:', err);
    } finally {
      setLogHistoryLoading(false);
    }
  }, []);

  const fetchSingleLog = useCallback(async (logId: string) => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/operation-log/${logId}`, { withCredentials: true });
      if (res.data?.log) setViewingLog(res.data.log);
    } catch (err) {
      showToast('Failed to load log', 'error');
    }
  }, [showToast]);

  const startSSEStream = useCallback(async (endpoint: string, actionKey: string) => {
    setActionLoading(actionKey);
    setLiveLog([]);
    liveLogRef.current = []; // Reset ref
    setLiveLogActive(true);
    const actionLabels: Record<string, string> = { bulk: 'bulk send', auto: 'full automation', outreach: 'stuck-at-docs outreach' };
    addLogLine(`Starting ${actionLabels[actionKey] || actionKey}...`, 'info');

    // Create abort controller for this stream
    const ac = new AbortController();
    sseAbortRef.current = ac;

    try {
      const response = await fetch(`${API_BASE}/api/admin/recovery/${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'text/event-stream' },
        signal: ac.signal,
      });

      if (!response.ok) {
        addLogLine(`Error: Server responded with status ${response.status}`, 'error');
        showToast(`Operation failed (HTTP ${response.status})`, 'error');
        setActionLoading(null);
        setLiveLogActive(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        addLogLine('Error: Could not read response stream', 'error');
        setActionLoading(null);
        setLiveLogActive(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'done') {
                addLogLine(data.success ? 'Operation completed successfully.' : 'Operation finished with errors.', data.success ? 'complete' : 'error');
              } else {
                addLogLine(data.message || JSON.stringify(data), data.type || 'info');
              }
            } catch (_) {
              // ignore malformed SSE
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        addLogLine('Operation killed by user.', 'error');
        showToast('Operation stopped', 'info');
      } else {
        addLogLine(`Connection error: ${err.message}`, 'error');
        showToast('Stream connection failed', 'error');
      }
    } finally {
      sseAbortRef.current = null;
      setActionLoading(null);
      setLiveLogActive(false);
      // Save the log for later reference — use ref for reliable access to accumulated lines
      const opTypeMap: Record<string, string> = { bulk: 'bulk_send', auto: 'auto_run', outreach: 'outreach' };
      const currentLog = liveLogRef.current;
      console.log('[startSSEStream finally] liveLogRef lines:', currentLog.length, 'actionKey:', actionKey);
      if (currentLog.length > 0) {
        const wasKilled = currentLog.some(l => l.message.includes('killed') || l.message.includes('Kill') || l.message.includes('stopped'));
        console.log('[startSSEStream finally] Calling saveOperationLog, wasKilled:', wasKilled);
        await saveOperationLog(opTypeMap[actionKey] || actionKey, wasKilled ? 'killed' : 'completed', currentLog);
      } else {
        console.log('[startSSEStream finally] No log lines to save!');
      }
      await fetchDashboard();
      await fetchActivityLog(1, activityEventFilter);
    }
  }, [addLogLine, showToast, fetchDashboard, fetchActivityLog, activityEventFilter, saveOperationLog]);

  const handleBulkSend = () => startSSEStream('send-bulk-stream', 'bulk');
  const handleAutoRun = () => startSSEStream('auto-run-stream', 'auto');

  const handleExportCSV = async () => {
    setActionLoading('export');
    try {
      const res = await axios.get(`${API_BASE}/api/admin/recovery/export-csv`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recovery-campaign-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('CSV exported successfully', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Export failed', 'error');
    } finally { setActionLoading(null); }
  };

  const handleSendReminder = async (campaignUserId: string) => {
    setSendingReminder(campaignUserId);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/recovery/send-reminder`, { campaignUserId }, { withCredentials: true });
      showToast(res.data?.message || 'Reminder sent', 'success');
      await fetchDashboard();
      await fetchActivityLog(1, activityEventFilter);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send reminder', 'error');
    } finally { setSendingReminder(null); }
  };

  const handleSaveSettings = async () => {
    setActionLoading('settings');
    try {
      await axios.post(`${API_BASE}/api/admin/recovery/settings`, {
        enabled: settings.enabled,
        scanIntervalHours: settings.scanIntervalHours,
        reminder1DelayDays: settings.reminder1DelayDays,
        reminder2DelayDays: settings.reminder2DelayDays,
        finalReminderDelayDays: settings.finalReminderDelayDays,
        abandonmentThresholdHours: settings.abandonmentThresholdHours,
      }, { withCredentials: true });
      showToast('Settings saved successfully', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to save settings', 'error');
    } finally { setActionLoading(null); }
  };

  const handleTestEmail = async (stage: string) => {
    setTestLoading(stage);
    setTestResults([]);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/recovery/test-email`, { stage }, { withCredentials: true });
      if (res.data?.results) setTestResults(res.data.results);
      showToast(res.data?.message || 'Test email sent', res.data?.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send test email', 'error');
    } finally { setTestLoading(null); }
  };

  const handleTestOutreach = async () => {
    setTestLoading('outreach_docs');
    setTestResults([]);
    try {
      const res = await axios.post(`${API_BASE}/api/admin/recovery/test-outreach-email`, {}, { withCredentials: true });
      if (res.data?.results) setTestResults(res.data.results);
      showToast(res.data?.message || 'Test outreach email sent', res.data?.success ? 'success' : 'error');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to send test outreach email', 'error');
    } finally { setTestLoading(null); }
  };

  // ─── Funnel Data ────────────────────────────────────────────────────────────
  const funnelSteps = [
    { label: 'Emails Sent', count: funnel.emailsSent, color: '#3b82f6', width: '100%' },
    { label: 'Emails Opened', count: funnel.emailsOpened, color: '#6366f1', width: '85%' },
    { label: 'Links Clicked', count: funnel.linksClicked, color: '#8b5cf6', width: '70%' },
    { label: 'Portal Visits', count: funnel.portalVisits, color: '#a855f7', width: '55%' },
    { label: 'App Started', count: funnel.applicationsCreated, color: '#f59e0b', width: '40%' },
    { label: 'Payment Complete', count: funnel.paymentsCompleted, color: '#10b981', width: '28%' },
  ];

  if (loading) {
    return (
      <LoadingOverlay>
        <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
      </LoadingOverlay>
    );
  }

  return (
    <PageContainer>
      {/* Toast */}
      {toast.show && (
        <ToastContainer $type={toast.type}>
          {toast.message}
        </ToastContainer>
      )}

      {/* Disabled Banner */}
      {!settings.enabled && (
        <div style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '1px solid #fecaca',
          borderLeft: '4px solid #ef4444',
          borderRadius: 10,
          padding: '14px 20px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%',
            background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, flexShrink: 0,
          }}>
            <span style={{ filter: 'grayscale(0)' }}>&#9724;</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>
              Recovery System is Paused
            </div>
            <div style={{ fontSize: 12, color: '#b91c1c', lineHeight: 1.5 }}>
              Automated recovery emails are <strong>not being sent</strong>. The scheduler is stopped — no scans or reminders will run until re-enabled.
              {settings.lastAutoRunAt && <span> Last auto-run was <strong>{timeAgo(settings.lastAutoRunAt)}</strong>.</span>}
            </div>
          </div>
          <button onClick={async () => {
            setToggleLoading(true);
            setSettings(prev => ({ ...prev, enabled: true }));
            try {
              const resp = await axios.post(`${API_BASE}/api/admin/recovery/settings`, { enabled: true }, { withCredentials: true });
              if (resp.data.schedulerRunning !== undefined) setSchedulerRunning(resp.data.schedulerRunning);
              showToast('Recovery system enabled — scheduler started', 'success');
            } catch {
              setSettings(prev => ({ ...prev, enabled: false }));
              showToast('Failed to enable', 'error');
            } finally {
              setToggleLoading(false);
            }
          }} style={{
            background: '#10b981', color: '#fff', border: 'none', borderRadius: 6,
            padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            Enable Now
          </button>
        </div>
      )}

      {/* Enabled Banner */}
      {settings.enabled && schedulerRunning && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          border: '1px solid #a7f3d0',
          borderLeft: '4px solid #10b981',
          borderRadius: 10,
          padding: '10px 20px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <PulseDot />
          <span style={{ fontSize: 12, color: '#065f46', fontWeight: 500 }}>
            Recovery system is <strong>active</strong> — scheduler running every <strong>{settings.scanIntervalHours}h</strong>
            {settings.lastAutoRunAt && <span> &middot; last run {timeAgo(settings.lastAutoRunAt)}</span>}
          </span>
        </div>
      )}

      {/* Timeline Modal */}
      {timelineData && (
        <ModalOverlay onClick={() => setTimelineData(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <CardHeaderTitle style={{ marginBottom: 4 }}>
                  User Activity Timeline
                </CardHeaderTitle>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {timelineData.campaignUser.name || 'N/A'} &bull; {timelineData.campaignUser.email}
                </div>
              </div>
              <CloseButton onClick={() => setTimelineData(null)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              {/* User summary */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                  <strong>Status:</strong> {timelineData.campaignUser.applicationStatus?.replace(/_/g, ' ')}
                </div>
                <div style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                  <strong>Email Stage:</strong> {timelineData.campaignUser.emailStage?.replace(/_/g, ' ')}
                </div>
                <div style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                  <strong>Active:</strong> {timelineData.campaignUser.campaignActive ? 'Yes' : 'No'}
                </div>
                <div style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                  <strong>Progress:</strong> {timelineData.campaignUser.applicationProgress?.percent || 0}%
                </div>
              </div>

              {timelineData.events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>
                  No activity events recorded yet for this user.
                </div>
              ) : (
                <TimelineContainer>
                  {timelineData.events.map((event, idx) => {
                    const style = getEventStyle(event.eventType);
                    return (
                      <TimelineItem key={event._id || idx}>
                        <TimelineDot $color={style.color} />
                        <TimelineTime>{formatDate(event.createdAt)}</TimelineTime>
                        <TimelineContent>
                          <EventBadge $color={style.color} $bg={style.bg}>
                            {style.icon} {style.label}
                          </EventBadge>
                          {event.campaignStage && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#64748b' }}>
                              Stage: {event.campaignStage.replace(/_/g, ' ')}
                            </span>
                          )}
                          {event.subject && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                              Subject: {event.subject}
                            </div>
                          )}
                          {event.metadata?.feedbackLabel && (
                            <div style={{ fontSize: 11, color: '#be185d', marginTop: 4, fontWeight: 600 }}>
                              Feedback: {event.metadata.feedbackLabel}
                            </div>
                          )}
                          {event.metadata?.explanation && (
                            <div style={{
                              fontSize: 12, color: '#334155', marginTop: 4, padding: '8px 12px',
                              background: '#fdf2f8', border: '1px solid #fce7f3', borderRadius: 6,
                              lineHeight: 1.5, fontStyle: 'italic',
                            }}>
                              "{event.metadata.explanation}"
                            </div>
                          )}
                          {event.metadata?.applicationId && event.eventType?.startsWith('outreach') && (
                            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                              App: {event.metadata.applicationId}
                            </div>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </TimelineContainer>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Exclude User Modal */}
      {excludeModalUser && (
        <ModalOverlay onClick={() => setExcludeModalUser(null)}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ width: 480 }}>
            <ModalHeader>
              <div>
                <CardHeaderTitle style={{ marginBottom: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" style={{ marginRight: 6, verticalAlign: 'middle' }}>
                    <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                  Exclude User from Recovery
                </CardHeaderTitle>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  <strong>{excludeModalUser.name || 'N/A'}</strong> &mdash; {excludeModalUser.email}
                </div>
              </div>
              <CloseButton onClick={() => setExcludeModalUser(null)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody style={{ padding: 20 }}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Reason for Exclusion <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  value={excludeReason}
                  onChange={e => setExcludeReason(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: 13, background: '#f8fafc', color: '#334155', outline: 'none',
                  }}
                >
                  <option value="">Select a reason...</option>
                  <option value="location_not_eligible">Location Not Eligible</option>
                  <option value="not_interested">User Not Interested</option>
                  <option value="duplicate_account">Duplicate Account</option>
                  <option value="already_applied_elsewhere">Already Applied Elsewhere</option>
                  <option value="incomplete_documents">Cannot Provide Required Documents</option>
                  <option value="business_not_eligible">Business/Vehicle Not Eligible</option>
                  <option value="user_requested">User Requested Removal</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Additional Notes (optional)
                </label>
                <textarea
                  value={excludeNote}
                  onChange={e => setExcludeNote(e.target.value)}
                  placeholder="Add any additional context about why this user is being excluded..."
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0',
                    fontSize: 13, background: '#f8fafc', color: '#334155', resize: 'vertical',
                    fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e' }}>
                <strong>Note:</strong> Excluded users will not receive any recovery emails, outreach emails, or automated follow-ups. You can re-include them later from the Campaign Users table.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <SmallButton
                  onClick={() => setExcludeModalUser(null)}
                  style={{ background: '#f1f5f9', color: '#475569', padding: '8px 16px', fontSize: 12 }}
                >
                  Cancel
                </SmallButton>
                <SmallButton
                  onClick={handleExcludeUser}
                  disabled={!excludeReason || excludeLoading}
                  style={{
                    background: excludeReason ? '#dc2626' : '#fca5a5', color: '#fff', padding: '8px 16px', fontSize: 12,
                    opacity: excludeReason ? 1 : 0.6, cursor: excludeReason ? 'pointer' : 'not-allowed',
                  }}
                >
                  {excludeLoading ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Excluding...</>) : 'Exclude User'}
                </SmallButton>
              </div>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Tile Detail Modal */}
      {(tileModalData || tileModalLoading) && (
        <ModalOverlay onClick={() => { setTileModalData(null); setTileModalLoading(false); }}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ width: 720 }}>
            <ModalHeader>
              <div>
                <CardHeaderTitle style={{ marginBottom: 2 }}>
                  {tileModalLoading ? 'Loading...' : tileModalData?.title || 'Detail'}
                </CardHeaderTitle>
                {tileModalData && (
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {tileModalData.total} total records {tileModalData.items.length < tileModalData.total ? `(showing latest ${tileModalData.items.length})` : ''}
                  </div>
                )}
              </div>
              <CloseButton onClick={() => { setTileModalData(null); setTileModalLoading(false); }}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              {tileModalLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
                </div>
              ) : tileModalData && tileModalData.items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
                  No records found.
                </div>
              ) : tileModalData ? (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {/* totalAbandoned */}
                  {tileModalData.metric === 'totalAbandoned' && (
                    <Table>
                      <thead>
                        <tr><Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Stage</Th><Th>Progress</Th><Th>Active</Th><Th>Added</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => {
                          const ss = getStatusStyle(item.status);
                          return (
                            <Tr key={i}>
                              <Td style={{ fontWeight: 500 }}>{item.name}</Td>
                              <Td style={{ fontSize: 12 }}>{item.email}</Td>
                              <Td><StatusBadge $color={ss.color} $bg={ss.bg}>{(item.status || '').replace(/_/g, ' ')}</StatusBadge></Td>
                              <Td><StageBadge $color={getStageColor(item.emailStage)}>{(item.emailStage || 'none').replace(/_/g, ' ')}</StageBadge></Td>
                              <Td>
                                <ProgressBarContainer>
                                  <ProgressBarTrack><ProgressBarFill $percent={item.progress} $color={getProgressColor(item.progress)} /></ProgressBarTrack>
                                  <ProgressText>{item.progress}%</ProgressText>
                                </ProgressBarContainer>
                              </Td>
                              <Td>
                                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: item.active ? '#10b981' : '#cbd5e1' }} />
                              </Td>
                              <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.createdAt)}</Td>
                            </Tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}

                  {/* emailsSent */}
                  {tileModalData.metric === 'emailsSent' && (
                    <Table>
                      <thead>
                        <tr><Th>Recipient</Th><Th>Name</Th><Th>Stage</Th><Th>Subject</Th><Th>Sent At</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => (
                          <Tr key={i}>
                            <Td style={{ fontSize: 12 }}>{item.recipientEmail}</Td>
                            <Td>{item.recipientName || '--'}</Td>
                            <Td><StageBadge $color={getStageColor(item.stage)}>{(item.stage || '--').replace(/_/g, ' ')}</StageBadge></Td>
                            <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>{item.subject || '--'}</Td>
                            <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.sentAt)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* emailsOpened */}
                  {tileModalData.metric === 'emailsOpened' && (
                    <Table>
                      <thead>
                        <tr><Th>Recipient</Th><Th>Name</Th><Th>Stage</Th><Th>Opened At</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => (
                          <Tr key={i}>
                            <Td style={{ fontSize: 12 }}>{item.recipientEmail}</Td>
                            <Td>{item.recipientName || '--'}</Td>
                            <Td><StageBadge $color={getStageColor(item.stage)}>{(item.stage || '--').replace(/_/g, ' ')}</StageBadge></Td>
                            <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.openedAt)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* linksClicked */}
                  {tileModalData.metric === 'linksClicked' && (
                    <Table>
                      <thead>
                        <tr><Th>Recipient</Th><Th>Name</Th><Th>Stage</Th><Th>Clicked At</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => (
                          <Tr key={i}>
                            <Td style={{ fontSize: 12 }}>{item.recipientEmail}</Td>
                            <Td>{item.recipientName || '--'}</Td>
                            <Td><StageBadge $color={getStageColor(item.stage)}>{(item.stage || '--').replace(/_/g, ' ')}</StageBadge></Td>
                            <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.clickedAt)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* portalVisits */}
                  {tileModalData.metric === 'portalVisits' && (
                    <Table>
                      <thead>
                        <tr><Th>Recipient</Th><Th>Name</Th><Th>Visited At</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => (
                          <Tr key={i}>
                            <Td style={{ fontSize: 12 }}>{item.recipientEmail}</Td>
                            <Td>{item.recipientName || '--'}</Td>
                            <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.visitedAt)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* conversions */}
                  {tileModalData.metric === 'conversions' && (
                    <Table>
                      <thead>
                        <tr><Th>Name</Th><Th>Email</Th><Th>App ID</Th><Th>Email Stage</Th><Th>Completed At</Th></tr>
                      </thead>
                      <tbody>
                        {tileModalData.items.map((item: any, i: number) => (
                          <Tr key={i}>
                            <Td style={{ fontWeight: 500 }}>{item.name}</Td>
                            <Td style={{ fontSize: 12 }}>{item.email}</Td>
                            <Td>
                              {item.applicationId ? (
                                <code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>{item.applicationId}</code>
                              ) : '--'}
                            </Td>
                            <Td><StageBadge $color={getStageColor(item.emailStage)}>{(item.emailStage || 'none').replace(/_/g, ' ')}</StageBadge></Td>
                            <Td style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(item.completedAt)}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              ) : null}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Section 1: Summary Cards */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 10px 0' }}>
        <SectionTitle style={{ margin: 0 }}>Recovery Overview</SectionTitle>
        <RefreshIcon $spinning={refreshingOverview} onClick={handleRefreshOverview} disabled={refreshingOverview} title="Refresh overview metrics">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2.5 11.5a10 10 0 0118.8-4.3M21.5 12.5a10 10 0 01-18.8 4.2" />
          </svg>
        </RefreshIcon>
      </div>
      <SummaryGrid>
        <MiniCard $accent="#f97316" onClick={() => fetchTileDetail('totalAbandoned')}>
          <IconWrapper $bg="linear-gradient(135deg, #f97316 0%, #ea580c 100%)">
            <span style={{ color: '#fff' }}>&#9888;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Total Abandoned</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.totalAbandoned} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
        <MiniCard $accent="#3b82f6" onClick={() => fetchTileDetail('emailsSent')}>
          <IconWrapper $bg="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
            <span style={{ color: '#fff' }}>&#9993;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Emails Sent</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.emailsSent} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
        <MiniCard $accent="#10b981" onClick={() => fetchTileDetail('emailsOpened')}>
          <IconWrapper $bg="linear-gradient(135deg, #10b981 0%, #059669 100%)">
            <span style={{ color: '#fff' }}>&#128233;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Emails Opened</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.emailsOpened} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
        <MiniCard $accent="#8b5cf6" onClick={() => fetchTileDetail('linksClicked')}>
          <IconWrapper $bg="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
            <span style={{ color: '#fff' }}>&#128279;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Links Clicked</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.linksClicked} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
        <MiniCard $accent="#06b6d4" onClick={() => fetchTileDetail('portalVisits')}>
          <IconWrapper $bg="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)">
            <span style={{ color: '#fff' }}>&#127760;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Portal Visits</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.portalVisits} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
        <MiniCard $accent="#10b981" onClick={() => fetchTileDetail('conversions')}>
          <IconWrapper $bg="linear-gradient(135deg, #10b981 0%, #059669 100%)">
            <span style={{ color: '#fff' }}>&#10003;</span>
          </IconWrapper>
          <MiniCardContent>
            <MiniLabel>Conversions</MiniLabel>
            <MiniValue><AnimatedCounter value={funnel.paymentsCompleted} /></MiniValue>
          </MiniCardContent>
        </MiniCard>
      </SummaryGrid>

      {/* Section 2: Funnel + Live Monitor */}
      <TwoColumnRow>
        <Card>
          <CardHeader>
            <CardHeaderTitle>
              <span style={{ fontSize: 16 }}>&#9660;</span>
              Conversion Funnel
            </CardHeaderTitle>
            <TimestampText>
              {funnel.emailsSent > 0
                ? `${((funnel.paymentsCompleted / funnel.emailsSent) * 100).toFixed(1)}% overall conversion`
                : 'No data yet'}
            </TimestampText>
          </CardHeader>
          <CardBody>
            {funnelSteps.map((step, i) => {
              const prevCount = i > 0 ? funnelSteps[i - 1].count : step.count;
              const pct = prevCount > 0 ? ((step.count / prevCount) * 100).toFixed(1) : '0.0';
              return (
                <FunnelStep key={step.label} $width={step.width} $color={step.color}>
                  <FunnelLabel>{step.label}</FunnelLabel>
                  <FunnelMeta>
                    <strong>{step.count}</strong>
                    {i > 0 && <span>({pct}%)</span>}
                  </FunnelMeta>
                </FunnelStep>
              );
            })}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardHeaderTitle>
              <LiveDot />
              Live Abandonment Monitor
            </CardHeaderTitle>
            <TimestampText>Auto-refreshes every 15s</TimestampText>
          </CardHeader>
          <CardBody style={{ padding: '14px' }}>
            <MonitorSection>
              <MonitorHeader $color="#3b82f6">
                <span>Currently Filling</span>
                <CountBadge>{liveData.currentlyFilling.length}</CountBadge>
              </MonitorHeader>
              <MonitorList>
                {liveData.currentlyFilling.length === 0 ? (
                  <MonitorEmpty>No active users in the last 30 min</MonitorEmpty>
                ) : (
                  liveData.currentlyFilling.map((u: any, i: number) => (
                    <MonitorItem key={u._id || u.email || i}>
                      <span style={{ fontWeight: 500 }}>{u.email || 'Unknown'}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#94a3b8' }}>
                        {u.applicationId && <code style={{ fontSize: 10, background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{u.applicationId}</code>}
                        {timeAgo(u.lastActivity || u.updatedAt)}
                      </span>
                    </MonitorItem>
                  ))
                )}
              </MonitorList>
            </MonitorSection>
            <MonitorSection>
              <MonitorHeader $color="#f59e0b">
                <span>Stuck at Documents</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {liveData.stuckAtDocuments.length > 0 && (
                    <button
                      onClick={() => startSSEStream('outreach-stuck-docs-stream', 'outreach')}
                      disabled={!!actionLoading}
                      style={{
                        padding: '3px 10px', fontSize: 10, fontWeight: 600, border: '1px solid #f59e0b',
                        borderRadius: 6, background: 'rgba(245,158,11,0.1)', color: '#b45309', cursor: actionLoading ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                      }}
                      title="Send outreach emails to all stuck-at-documents users asking for feedback"
                    >
                      {actionLoading === 'outreach' ? <><CircularProgress size={10} /> Sending...</> : <><span>&#9993;</span> Send Outreach to All</>}
                    </button>
                  )}
                  <CountBadge>{liveData.stuckAtDocuments.length}</CountBadge>
                </div>
              </MonitorHeader>
              <MonitorList>
                {liveData.stuckAtDocuments.length === 0 ? (
                  <MonitorEmpty>No users stuck at document upload</MonitorEmpty>
                ) : (
                  liveData.stuckAtDocuments.map((u: any, i: number) => (
                    <MonitorItem key={u._id || u.email || i}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 500, fontSize: 12 }}>{u.email || 'Unknown'}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {u.applicationId && <code style={{ fontSize: 10, background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{u.applicationId}</code>}
                          {timeAgo(u.lastActivity || u.updatedAt)}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            setSendingReminder(u.applicationId);
                            await axios.post(`${API_BASE}/api/admin/recovery/send-single-outreach`, { applicationId: u.applicationId, email: u.email }, { withCredentials: true });
                            showToast(`Outreach sent to ${u.email}`, 'success');
                          } catch (err: any) {
                            showToast(err.response?.data?.message || 'Failed to send outreach', 'error');
                          } finally {
                            setSendingReminder(null);
                          }
                        }}
                        disabled={sendingReminder === u.applicationId}
                        style={{
                          padding: '2px 8px', fontSize: 9, fontWeight: 600, border: '1px solid #e2e8f0',
                          borderRadius: 4, background: '#f8fafc', color: '#475569', cursor: 'pointer',
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                        title={`Send outreach email to ${u.email}`}
                      >
                        {sendingReminder === u.applicationId ? '...' : '✉ Ask'}
                      </button>
                    </MonitorItem>
                  ))
                )}
              </MonitorList>
            </MonitorSection>
            <MonitorSection>
              <MonitorHeader $color="#ef4444">
                <span>Stuck at Payment</span>
                <CountBadge>{liveData.stuckAtPayment.length}</CountBadge>
              </MonitorHeader>
              <MonitorList>
                {liveData.stuckAtPayment.length === 0 ? (
                  <MonitorEmpty>No users stuck at payment</MonitorEmpty>
                ) : (
                  liveData.stuckAtPayment.map((u: any, i: number) => (
                    <MonitorItem key={u._id || u.email || i}>
                      <span style={{ fontWeight: 500 }}>{u.email || 'Unknown'}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#94a3b8' }}>
                        {u.applicationId && <code style={{ fontSize: 10, background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{u.applicationId}</code>}
                        {timeAgo(u.lastActivity || u.updatedAt)}
                      </span>
                    </MonitorItem>
                  ))
                )}
              </MonitorList>
            </MonitorSection>
          </CardBody>
        </Card>
      </TwoColumnRow>

      {/* Section 3: Campaign Controls */}
      <SectionTitle>Campaign Controls</SectionTitle>
      {!settings.enabled && (
        <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
          System is paused — manual actions are still available but automated scheduling is stopped
        </div>
      )}
      <ControlsRow>
        <ActionButton $variant="blue" onClick={handleScan} disabled={actionLoading !== null}>
          {actionLoading === 'scan' ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : null}
          {actionLoading === 'scan' ? ' Scanning...' : 'Scan for Abandoned Users'}
        </ActionButton>
        <ActionButton $variant="purple" onClick={handleBulkSend} disabled={actionLoading !== null}>
          {actionLoading === 'bulk' ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : null}
          {actionLoading === 'bulk' ? ' Sending...' : 'Send All Eligible Reminders'}
        </ActionButton>
        <ActionButton $variant="green" onClick={handleAutoRun} disabled={actionLoading !== null}>
          {actionLoading === 'auto' ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : null}
          {actionLoading === 'auto' ? ' Running...' : 'Run Full Automation'}
        </ActionButton>
        {(actionLoading === 'bulk' || actionLoading === 'auto' || actionLoading === 'outreach') && (
          <ActionButton
            onClick={handleKillOperation}
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: '#fff',
              border: 'none',
              animation: 'none',
            }}
          >
            &#9632; Stop Operation
          </ActionButton>
        )}
        <ActionButton $variant="outline" onClick={handleExportCSV} disabled={actionLoading !== null}>
          {actionLoading === 'export' ? <CircularProgress size={14} sx={{ color: '#475569', mr: 0.5 }} /> : null}
          {actionLoading === 'export' ? ' Exporting...' : 'Export CSV'}
        </ActionButton>
        <ActionButton
          $variant="outline"
          onClick={() => { setLogHistoryVisible(true); fetchLogHistory(); }}
          style={{ fontSize: 12 }}
        >
          &#128203; Log History
        </ActionButton>
      </ControlsRow>

      {/* Live Log Panel */}
      {(liveLog.length > 0 || liveLogActive) && (
        <LogPanel>
          <LogHeader>
            <LogHeaderTitle>
              {liveLogActive && <LiveDot />}
              Live Operation Log
              {liveLogActive && <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}> (streaming...)</span>}
            </LogHeaderTitle>
            <div style={{ display: 'flex', gap: 6 }}>
              {liveLogActive && (
                <SmallButton
                  onClick={handleKillOperation}
                  style={{ background: '#ef4444', color: '#fff', fontSize: 10, padding: '3px 10px' }}
                >
                  &#9632; Kill
                </SmallButton>
              )}
              {!liveLogActive && (
                <>
                  <SmallButton
                    onClick={() => { setLogHistoryVisible(true); fetchLogHistory(); }}
                    style={{ background: '#1e293b', color: '#94a3b8', fontSize: 10, padding: '3px 10px', border: '1px solid #334155' }}
                  >
                    &#128203; History
                  </SmallButton>
                  <SmallButton
                    onClick={() => setLiveLog([])}
                    style={{ background: '#334155', color: '#94a3b8', fontSize: 10, padding: '3px 10px' }}
                  >
                    Clear Log
                  </SmallButton>
                </>
              )}
            </div>
          </LogHeader>
          <LogBody>
            {liveLog.map((line, i) => {
              let color = '#94a3b8';
              if (line.type === 'sent' || line.type === 'complete') color = '#4ade80';
              else if (line.type === 'error') color = '#f87171';
              else if (line.type === 'new') color = '#38bdf8';
              else if (line.type === 'sending') color = '#fbbf24';
              else if (line.type === 'skip') color = '#64748b';
              else if (line.type === 'wait') color = '#a78bfa';
              else if (line.type === 'phase') color = '#e879f9';
              else if (line.type === 'info') color = '#e2e8f0';
              else if (line.type === 'update') color = '#2dd4bf';

              return (
                <LogEntry key={i} $color={color}>
                  <LogTime>{line.time}</LogTime>
                  {line.message}
                </LogEntry>
              );
            })}
            <div ref={logEndRef} />
          </LogBody>
        </LogPanel>
      )}

      {/* Log History Modal */}
      {logHistoryVisible && (
        <ModalOverlay onClick={() => { setLogHistoryVisible(false); setViewingLog(null); }}>
          <ModalContent onClick={e => e.stopPropagation()} style={{ width: 800, maxHeight: '85vh' }}>
            <ModalHeader>
              <CardHeaderTitle>{viewingLog ? '📋 Operation Log Details' : '📋 Operation Log History'}</CardHeaderTitle>
              <CloseButton onClick={() => { if (viewingLog) { setViewingLog(null); } else { setLogHistoryVisible(false); } }}>
                {viewingLog ? '←' : '×'}
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {viewingLog ? (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ padding: '6px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                      <strong>Type:</strong> {viewingLog.operationType.replace(/_/g, ' ')}
                    </div>
                    <div style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 12,
                      background: viewingLog.status === 'completed' ? '#f0fdf4' : viewingLog.status === 'killed' ? '#fef2f2' : '#fffbeb',
                      color: viewingLog.status === 'completed' ? '#166534' : viewingLog.status === 'killed' ? '#991b1b' : '#92400e',
                      fontWeight: 600,
                    }}>
                      {viewingLog.status === 'completed' ? '✅' : viewingLog.status === 'killed' ? '🛑' : '⚠️'} {viewingLog.status}
                    </div>
                    <div style={{ padding: '6px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 12 }}>
                      <strong>Date:</strong> {new Date(viewingLog.createdAt).toLocaleString('en-IN')}
                    </div>
                  </div>
                  {viewingLog.summary && (
                    <div style={{ padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#475569' }}>
                      {viewingLog.summary}
                    </div>
                  )}
                  <LogPanel style={{ margin: 0 }}>
                    <LogBody style={{ maxHeight: 400 }}>
                      {viewingLog.lines.map((line, i) => {
                        let color = '#94a3b8';
                        if (line.type === 'sent' || line.type === 'complete') color = '#4ade80';
                        else if (line.type === 'error') color = '#f87171';
                        else if (line.type === 'new') color = '#38bdf8';
                        else if (line.type === 'sending') color = '#fbbf24';
                        else if (line.type === 'skip') color = '#64748b';
                        else if (line.type === 'wait') color = '#a78bfa';
                        else if (line.type === 'phase') color = '#e879f9';
                        else if (line.type === 'info') color = '#e2e8f0';
                        else if (line.type === 'update') color = '#2dd4bf';
                        return (
                          <LogEntry key={i} $color={color}>
                            <LogTime>{line.time}</LogTime>
                            {line.message}
                          </LogEntry>
                        );
                      })}
                    </LogBody>
                  </LogPanel>
                </>
              ) : logHistoryLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
                </div>
              ) : logHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>
                  No operation logs recorded yet. Logs are saved automatically after each bulk operation completes.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {logHistory.map(log => {
                    const opLabels: Record<string, string> = { bulk_send: 'Bulk Send', auto_run: 'Full Automation', outreach: 'Stuck Docs Outreach', scan: 'Scan' };
                    const statusColors: Record<string, { bg: string; color: string; icon: string }> = {
                      completed: { bg: '#f0fdf4', color: '#166534', icon: '✅' },
                      killed: { bg: '#fef2f2', color: '#991b1b', icon: '🛑' },
                      failed: { bg: '#fef2f2', color: '#991b1b', icon: '❌' },
                      running: { bg: '#fffbeb', color: '#92400e', icon: '⏳' },
                    };
                    const sc = statusColors[log.status] || statusColors.completed;
                    return (
                      <div
                        key={log._id}
                        onClick={() => fetchSingleLog(log._id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                          background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: log.operationType === 'outreach' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                            log.operationType === 'auto_run' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                            'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          color: '#fff', fontSize: 16, flexShrink: 0,
                        }}>
                          {log.operationType === 'outreach' ? '📨' : log.operationType === 'auto_run' ? '⚡' : '📧'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                              {opLabels[log.operationType] || log.operationType}
                            </span>
                            <span style={{
                              fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                              background: sc.bg, color: sc.color,
                            }}>
                              {sc.icon} {log.status}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.summary || 'No summary'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {new Date(log.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>
                            {new Date(log.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </div>
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                            {log.lines?.length || 0} lines
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Settings */}
      <SettingsPanel>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: settingsExpanded ? 14 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Recovery System Settings</span>
            <ToggleSwitch $active={settings.enabled} style={{ opacity: toggleLoading ? 0.5 : 1, pointerEvents: toggleLoading ? 'none' : 'auto' }} onClick={async () => {
              const newEnabled = !settings.enabled;
              setToggleLoading(true);
              setSettings(prev => ({ ...prev, enabled: newEnabled }));
              try {
                const resp = await axios.post(`${API_BASE}/api/admin/recovery/settings`, { enabled: newEnabled }, { withCredentials: true });
                if (resp.data.schedulerRunning !== undefined) setSchedulerRunning(resp.data.schedulerRunning);
                showToast(
                  newEnabled
                    ? 'Recovery system enabled — scheduler started, emails will be sent automatically'
                    : 'Recovery system disabled — scheduler stopped, no automated emails will be sent',
                  newEnabled ? 'success' : 'info'
                );
              } catch (err: any) {
                setSettings(prev => ({ ...prev, enabled: !newEnabled }));
                showToast('Failed to update toggle', 'error');
              } finally {
                setToggleLoading(false);
              }
            }} />
            <span style={{ fontSize: 12, color: settings.enabled ? '#10b981' : '#ef4444', fontWeight: 600 }}>
              {toggleLoading ? 'Updating...' : settings.enabled ? 'Active' : 'Paused'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {settings.lastScanAt && <TimestampText>Last scan: {timeAgo(settings.lastScanAt)}</TimestampText>}
            {settings.lastAutoRunAt && <TimestampText>Last auto-run: {timeAgo(settings.lastAutoRunAt)}</TimestampText>}
            <SmallButton onClick={() => setSettingsExpanded(prev => !prev)} style={{ background: '#f1f5f9', color: '#475569' }}>
              {settingsExpanded ? 'Collapse' : 'Configure'}
            </SmallButton>
          </div>
        </div>
        {settingsExpanded && (
          <SettingsGrid>
            <SettingsField>
              <SettingsLabel>Scan Interval (hrs)</SettingsLabel>
              <NumberInput type="number" min={1} value={settings.scanIntervalHours} onChange={e => setSettings(prev => ({ ...prev, scanIntervalHours: Number(e.target.value) }))} />
            </SettingsField>
            <SettingsField>
              <SettingsLabel>Reminder 1 Delay (days)</SettingsLabel>
              <NumberInput type="number" min={0} value={settings.reminder1DelayDays} onChange={e => setSettings(prev => ({ ...prev, reminder1DelayDays: Number(e.target.value) }))} />
            </SettingsField>
            <SettingsField>
              <SettingsLabel>Reminder 2 Delay (days)</SettingsLabel>
              <NumberInput type="number" min={0} value={settings.reminder2DelayDays} onChange={e => setSettings(prev => ({ ...prev, reminder2DelayDays: Number(e.target.value) }))} />
            </SettingsField>
            <SettingsField>
              <SettingsLabel>Final Reminder Delay (days)</SettingsLabel>
              <NumberInput type="number" min={0} value={settings.finalReminderDelayDays} onChange={e => setSettings(prev => ({ ...prev, finalReminderDelayDays: Number(e.target.value) }))} />
            </SettingsField>
            <SettingsField>
              <SettingsLabel>Abandonment Threshold (hrs)</SettingsLabel>
              <NumberInput type="number" min={1} value={settings.abandonmentThresholdHours} onChange={e => setSettings(prev => ({ ...prev, abandonmentThresholdHours: Number(e.target.value) }))} />
            </SettingsField>
            <ActionButton $variant="blue" onClick={handleSaveSettings} disabled={actionLoading === 'settings'} style={{ padding: '8px 16px', fontSize: 12, alignSelf: 'flex-end' }}>
              {actionLoading === 'settings' ? 'Saving...' : 'Save Settings'}
            </ActionButton>
          </SettingsGrid>
        )}
      </SettingsPanel>

      {/* Section 4: Email Activity Audit Trail */}
      <SectionTitle>Email Activity Audit Trail ({activityLogTotal})</SectionTitle>
      <TableCard>
        <CardHeader>
          <CardHeaderTitle>
            <span style={{ fontSize: 15 }}>&#128220;</span>
            Audit Trail
          </CardHeaderTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PageSizeSelect
              value={activityEventFilter}
              onChange={e => { setActivityEventFilter(e.target.value); fetchActivityLog(1, e.target.value); }}
            >
              <option value="">All Events</option>
              <option value="email_sent">Email Sent</option>
              <option value="email_open">Email Opened</option>
              <option value="email_click">Link Clicked</option>
              <option value="portal_visit">Portal Visit</option>
              <option value="outreach_sent">Outreach Sent</option>
              <option value="outreach_feedback">User Feedback</option>
              <option value="outreach_open">Outreach Opened</option>
            </PageSizeSelect>
          </div>
        </CardHeader>
        <TableContainer style={{ maxHeight: 320 }}>
          {activityLogLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px 0' }}>
              <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
            </div>
          ) : activityLog.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>
              No email activity events recorded yet. Send reminders to start tracking.
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>User</Th>
                  <Th>Event</Th>
                  <Th>Stage</Th>
                  <Th>Subject</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {activityLog.map((event) => {
                  const evStyle = getEventStyle(event.eventType);
                  return (
                    <Tr key={event._id}>
                      <Td style={{ whiteSpace: 'nowrap', fontSize: 11, color: '#64748b' }}>
                        {formatDate(event.createdAt)}
                      </Td>
                      <Td>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{event.userEmail || event.recipientEmail || 'Unknown'}</div>
                        {event.userName || event.recipientName ? (
                          <div style={{ fontSize: 10, color: '#94a3b8' }}>{event.userName || event.recipientName}</div>
                        ) : null}
                      </Td>
                      <Td>
                        <EventBadge $color={evStyle.color} $bg={evStyle.bg}>
                          {evStyle.icon} {evStyle.label}
                        </EventBadge>
                      </Td>
                      <Td>
                        {event.campaignStage ? (
                          <StageBadge $color={getStageColor(event.campaignStage)}>
                            {event.campaignStage.replace(/_/g, ' ')}
                          </StageBadge>
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>--</span>
                        )}
                      </Td>
                      <Td style={{ maxWidth: 260, fontSize: 11, color: '#64748b' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {event.subject || '--'}
                        </div>
                        {event.metadata?.explanation && (
                          <div style={{
                            fontSize: 10, color: '#be185d', marginTop: 2, padding: '3px 6px',
                            background: '#fdf2f8', borderRadius: 4, fontStyle: 'italic',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                          title={event.metadata.explanation}
                          >
                            "{event.metadata.explanation}"
                          </div>
                        )}
                        {event.metadata?.applicationId && event.eventType?.startsWith('outreach') && (
                          <code style={{ fontSize: 9, color: '#94a3b8' }}>{event.metadata.applicationId}</code>
                        )}
                      </Td>
                      <Td>
                        <SmallButton
                          onClick={() => event.userId && fetchUserTimeline(event.userId)}
                          style={{
                            background: event.userId ? '#f1f5f9' : '#f8fafc',
                            color: event.userId ? '#475569' : '#cbd5e1',
                            fontSize: 10, padding: '3px 8px',
                            cursor: event.userId ? 'pointer' : 'not-allowed',
                          }}
                          disabled={timelineLoading || !event.userId}
                          title={event.userId ? 'View user timeline' : 'No user linked to this event'}
                        >
                          Timeline
                        </SmallButton>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </TableContainer>
        {activityLogTotalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              Page {activityLogPage} of {activityLogTotalPages} ({activityLogTotal} events)
            </PaginationInfo>
            <PaginationControls>
              <PageButton disabled={activityLogPage <= 1} onClick={() => fetchActivityLog(activityLogPage - 1, activityEventFilter)}>
                &laquo; Prev
              </PageButton>
              <PageButton disabled={activityLogPage >= activityLogTotalPages} onClick={() => fetchActivityLog(activityLogPage + 1, activityEventFilter)}>
                Next &raquo;
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableCard>

      {/* Section 5: Campaign Users Table with Pagination */}
      <SectionHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <SectionTitle style={{ margin: 0 }}>Campaign Users ({totalUsers})</SectionTitle>
          <RefreshIcon $spinning={refreshingUsers} onClick={handleRefreshUsers} disabled={refreshingUsers} title="Refresh campaign users">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2.5 11.5a10 10 0 0118.8-4.3M21.5 12.5a10 10 0 01-18.8 4.2" />
            </svg>
          </RefreshIcon>
          {excludedCount > 0 && (
            <button
              onClick={() => { setShowExcluded(!showExcluded); setCurrentPage(1); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                border: `1px solid ${showExcluded ? '#f97316' : '#e2e8f0'}`,
                background: showExcluded ? '#fff7ed' : '#f8fafc',
                color: showExcluded ? '#ea580c' : '#64748b',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              title={showExcluded ? 'Click to hide excluded users' : 'Click to show excluded users'}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
              </svg>
              {excludedCount} Excluded {showExcluded ? '(Shown)' : '(Hidden)'}
            </button>
          )}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 8, pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={campaignSearch}
              onChange={e => { setCampaignSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Search name, email, phone..."
              style={{
                padding: '5px 8px 5px 28px', borderRadius: 8, border: '1px solid #e2e8f0',
                fontSize: 11, width: campaignSearch ? 220 : 180, background: '#f8fafc', color: '#334155',
                outline: 'none', transition: 'width 0.2s, border-color 0.2s',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.width = '220px'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; if (!campaignSearch) e.currentTarget.style.width = '180px'; }}
            />
            {campaignSearch && (
              <button
                onClick={() => { setCampaignSearch(''); setCurrentPage(1); }}
                style={{
                  position: 'absolute', right: 6, background: 'none', border: 'none',
                  cursor: 'pointer', color: '#94a3b8', fontSize: 14, lineHeight: 1, padding: 0,
                }}
                title="Clear search"
              >
                &times;
              </button>
            )}
          </div>
        </div>
        {totalUsers > 0 && (
          <InlinePaginationControls>
            <span style={{ fontSize: 11, color: '#94a3b8', marginRight: 4 }}>
              {startIdx + 1}-{Math.min(startIdx + pageSize, totalUsers)} of {totalUsers}
            </span>
            <PageSizeSelect
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '3px 6px', fontSize: 11 }}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </PageSizeSelect>
            <PageButton disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} style={{ padding: '3px 8px', fontSize: 11 }}>
              &laquo;
            </PageButton>
            <span style={{ fontSize: 11, color: '#64748b', minWidth: 50, textAlign: 'center' }}>
              Page {currentPage}/{totalPages || 1}
            </span>
            <PageButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={{ padding: '3px 8px', fontSize: 11 }}>
              &raquo;
            </PageButton>
          </InlinePaginationControls>
        )}
      </SectionHeader>
      <TableCard>
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>User</Th>
                <Th>App ID</Th>
                <Th>Status</Th>
                <Th>Email Stage</Th>
                <Th>Last Email</Th>
                <Th>Active</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <Td colSpan={8} style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                    No campaign users found. Run a scan to detect abandoned applications.
                  </Td>
                </tr>
              ) : (
                paginatedUsers.map((user, idx) => {
                  const statusStyle = getStatusStyle(user.applicationStatus);
                  const stageColor = getStageColor(user.emailStage);
                  const progressColor = getProgressColor(user.applicationProgress?.percent || 0);
                  const isExcluded = !!user.excluded;
                  const canSendReminder = user.campaignActive && !isExcluded && user.applicationStatus !== 'completed';
                  const isSending = sendingReminder === user._id;
                  const excludeReasonLabels: Record<string, string> = {
                    location_not_eligible: 'Location',
                    not_interested: 'Not interested',
                    duplicate_account: 'Duplicate',
                    already_applied_elsewhere: 'Applied elsewhere',
                    incomplete_documents: 'Incomplete docs',
                    business_not_eligible: 'Biz ineligible',
                    user_requested: 'User requested',
                    other: 'Other',
                  };

                  return (
                    <Tr key={user._id} style={isExcluded ? { opacity: 0.55, background: '#fef2f2' } : {}}>
                      <Td style={{ color: '#94a3b8', fontWeight: 500 }}>{startIdx + idx + 1}</Td>
                      <Td>
                        <div style={{ lineHeight: 1.3 }}>
                          <div style={{ fontWeight: 500, color: isExcluded ? '#94a3b8' : '#1e293b', textDecoration: isExcluded ? 'line-through' : 'none', fontSize: 13 }}>
                            {user.name || 'N/A'}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', textDecoration: isExcluded ? 'line-through' : 'none' }}>
                            {user.email}
                          </div>
                        </div>
                      </Td>
                      <Td>
                        {user.applicationId ? (
                          <code style={{ fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>{user.applicationId}</code>
                        ) : (
                          <span style={{ color: '#cbd5e1' }}>--</span>
                        )}
                      </Td>
                      <Td>
                        {isExcluded ? (
                          <div>
                            <StatusBadge $color="#dc2626" $bg="#fef2f2">
                              excluded
                            </StatusBadge>
                            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }} title={user.excludedNote || ''}>
                              {excludeReasonLabels[user.excludedReason || ''] || user.excludedReason || ''}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <StatusBadge $color={statusStyle.color} $bg={statusStyle.bg}>
                              {user.applicationStatus.replace(/_/g, ' ')}
                            </StatusBadge>
                            <ProgressBarContainer style={{ marginTop: 4 }}>
                              <ProgressBarTrack>
                                <ProgressBarFill $percent={user.applicationProgress?.percent || 0} $color={progressColor} />
                              </ProgressBarTrack>
                              <ProgressText>{user.applicationProgress?.percent || 0}%</ProgressText>
                            </ProgressBarContainer>
                          </div>
                        )}
                      </Td>
                      <Td>
                        <StageBadge $color={isExcluded ? '#cbd5e1' : stageColor}>{user.emailStage.replace(/_/g, ' ')}</StageBadge>
                      </Td>
                      <Td><TimestampText>{timeAgo(user.lastEmailSentAt)}</TimestampText></Td>
                      <Td>
                        {isExcluded ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                            Excluded
                          </span>
                        ) : (
                          <>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: user.campaignActive ? '#10b981' : '#cbd5e1', marginRight: 4 }} />
                            <span style={{ fontSize: 11, color: user.campaignActive ? '#10b981' : '#94a3b8' }}>{user.campaignActive ? 'Yes' : 'No'}</span>
                          </>
                        )}
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {isExcluded ? (
                            <SmallButton
                              onClick={() => handleIncludeUser(user)}
                              style={{ background: '#ecfdf5', color: '#059669', fontSize: 10, padding: '4px 8px', border: '1px solid #a7f3d0' }}
                              title="Re-include this user in recovery campaigns"
                            >
                              Re-include
                            </SmallButton>
                          ) : (
                            <>
                              {canSendReminder ? (
                                <SmallButton onClick={() => handleSendReminder(user._id)} disabled={isSending || actionLoading !== null}>
                                  {isSending ? (<><CircularProgress size={10} sx={{ color: '#fff', mr: 0.5 }} />{' Sending'}</>) : ('Send')}
                                </SmallButton>
                              ) : (
                                <span style={{ fontSize: 11, color: '#cbd5e1' }}>--</span>
                              )}
                              <SmallButton
                                onClick={() => { setExcludeModalUser(user); setExcludeReason(''); setExcludeNote(''); }}
                                style={{ background: '#fef2f2', color: '#dc2626', fontSize: 10, padding: '4px 8px', border: '1px solid #fecaca' }}
                                title="Exclude this user from recovery"
                              >
                                Exclude
                              </SmallButton>
                            </>
                          )}
                          <SmallButton
                            onClick={() => fetchUserTimeline(user.userId)}
                            style={{ background: '#f1f5f9', color: '#475569', fontSize: 10, padding: '4px 8px' }}
                            disabled={timelineLoading}
                          >
                            Activity
                          </SmallButton>
                        </div>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalUsers > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              <span>Showing {startIdx + 1}-{Math.min(startIdx + pageSize, totalUsers)} of {totalUsers}</span>
              <span>
                Rows per page:
                <PageSizeSelect
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ marginLeft: 6 }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </PageSizeSelect>
              </span>
            </PaginationInfo>
            <PaginationControls>
              <PageButton disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                &laquo; Prev
              </PageButton>
              {getPageNumbers(currentPage, totalPages).map((p, i) =>
                typeof p === 'string' ? (
                  <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#94a3b8', fontSize: 12 }}>{p}</span>
                ) : (
                  <PageButton key={p} $active={p === currentPage} onClick={() => setCurrentPage(p)}>
                    {p}
                  </PageButton>
                )
              )}
              <PageButton disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                Next &raquo;
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        )}
      </TableCard>

      {/* Test Recovery Emails — Collapsible, at bottom */}
      <Card style={{ marginBottom: 20 }}>
        <CollapsibleCardHeader onClick={() => setTestCardExpanded(prev => !prev)}>
          <CardHeaderTitle>
            <span style={{ fontSize: 16 }}>&#128231;</span>
            Test Recovery Emails
            <TimestampText style={{ marginLeft: 8 }}>Sends test emails to admin inboxes only</TimestampText>
          </CardHeaderTitle>
          <ChevronIcon $expanded={testCardExpanded}>&#9660;</ChevronIcon>
        </CollapsibleCardHeader>
        {testCardExpanded && (
          <CardBody>
            {/* Recovery Reminder Tests */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Recovery Reminders</span>
                <span style={{ fontSize: 10, color: '#94a3b8', background: '#f1f5f9', padding: '2px 8px', borderRadius: 10 }}>
                  For abandoned users who haven't started or completed applications
                </span>
              </div>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 0, marginBottom: 14, lineHeight: 1.6 }}>
                Send test versions of each recovery email stage to admin inboxes. These include a
                <strong> [TEST] </strong> banner, simulated user data, and working tracking pixels/links so you can verify
                the full recovery flow end-to-end.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <ActionButton $variant="blue" onClick={() => handleTestEmail('reminder_1')} disabled={testLoading !== null} style={{ padding: '9px 18px', fontSize: 12 }}>
                  {testLoading === 'reminder_1' ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Sending...</>) : (<>&#9993; Test Reminder 1</>)}
                </ActionButton>
                <ActionButton $variant="purple" onClick={() => handleTestEmail('reminder_2')} disabled={testLoading !== null} style={{ padding: '9px 18px', fontSize: 12 }}>
                  {testLoading === 'reminder_2' ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Sending...</>) : (<>&#9993; Test Reminder 2</>)}
                </ActionButton>
                <ActionButton onClick={() => handleTestEmail('final_reminder')} disabled={testLoading !== null} style={{ padding: '9px 18px', fontSize: 12, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff', border: 'none' }}>
                  {testLoading === 'final_reminder' ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Sending...</>) : (<>&#9993; Test Final Reminder</>)}
                </ActionButton>
                <ActionButton $variant="green" onClick={() => handleTestEmail('all')} disabled={testLoading !== null} style={{ padding: '9px 18px', fontSize: 12 }}>
                  {testLoading === 'all' ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Sending All...</>) : (<>&#128232; Send All 3 Reminders</>)}
                </ActionButton>
              </div>
            </div>

            {/* Divider */}
            <hr style={{ border: 'none', borderTop: '1px dashed #e2e8f0', margin: '4px 0 20px' }} />

            {/* Stuck-at-Documents Outreach Test */}
            <div style={{ marginBottom: testResults.length > 0 ? 14 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Stuck-at-Documents Outreach</span>
                <span style={{ fontSize: 10, color: '#b45309', background: '#fffbeb', padding: '2px 8px', borderRadius: 10, border: '1px solid #fde68a' }}>
                  For users stuck at document upload step
                </span>
              </div>
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 0, marginBottom: 14, lineHeight: 1.6 }}>
                Test the outreach email sent to users whose applications are stuck at document upload. The email contains
                <strong> 6 clickable feedback buttons </strong> so users can tell you what's holding them back. Clicking any button
                opens a <strong>secure feedback page</strong> where they can optionally provide more details. All feedback appears
                in the Audit Trail and User Timeline.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <ActionButton onClick={handleTestOutreach} disabled={testLoading !== null} style={{
                  padding: '9px 18px', fontSize: 12,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff', border: 'none',
                }}>
                  {testLoading === 'outreach_docs' ? (<><CircularProgress size={12} sx={{ color: '#fff', mr: 0.5 }} /> Sending...</>) : (<>&#128232; Test Outreach Email</>)}
                </ActionButton>
              </div>
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  What the outreach email includes:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 6 }}>
                  {[
                    { emoji: '📄', label: "I'm confused about documents" },
                    { emoji: '⏳', label: 'My documents are not ready' },
                    { emoji: '🔧', label: 'Facing a technical issue' },
                    { emoji: '🤝', label: 'I need someone to help' },
                    { emoji: '✅', label: "I'll complete it soon" },
                    { emoji: '❌', label: 'No longer interested' },
                  ].map((opt, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                      background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 11, color: '#475569',
                    }}>
                      <span>{opt.emoji}</span> {opt.label}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
                  Each button links to a secure HMAC-signed feedback page. Users can optionally add a detailed explanation.
                  Responses are logged as <strong>outreach_feedback</strong> events in the Audit Trail.
                </div>
              </div>
            </div>

            {/* Test Results */}
            {testResults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
                {testResults.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 6, fontSize: 12,
                    background: r.success ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${r.success ? '#bbf7d0' : '#fecaca'}`,
                    color: r.success ? '#166534' : '#991b1b',
                  }}>
                    <span style={{ fontSize: 14 }}>{r.success ? '\u2705' : '\u274C'}</span>
                    <strong>{r.stage.replace(/_/g, ' ')}</strong>
                    <span style={{ color: '#64748b' }}>{r.success ? `\u2014 Sent to ${r.sentTo?.join(', ')}` : '\u2014 Failed'}</span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        )}
      </Card>
    </PageContainer>
  );
};

export default AdminApplicationRecovery;
